import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { daysAgo, parseYmd, shiftRange, thisMonth, today, wholeMonths, ymd } from '../lib/dates.js';

// Spelt out rather than from toLocaleDateString, which gives "Sept" in en-IN.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const mon = (d) => MONTHS[d.getMonth()];
const monYear = (d) => `${mon(d)} ${d.getFullYear()}`;
const dayMon = (d) => `${d.getDate()} ${mon(d)}`;

/** "Oct 2026", "Jul–Sep 2026", "2026", "FY 2026-27" or "30 Sep – 6 Oct 2026": the range as a person says it. */
export function rangeLabel(from, to) {
  const f = parseYmd(from);
  const t = parseYmd(to);
  const months = wholeMonths(from, to);
  const sameYear = f.getFullYear() === t.getFullYear();
  if (months === 1) return monYear(f);
  if (months === 12 && f.getMonth() === 0) return String(f.getFullYear());
  if (months === 12 && f.getMonth() === 3) return `FY ${f.getFullYear()}-${String(t.getFullYear()).slice(2)}`;
  if (months) return sameYear ? `${mon(f)}–${monYear(t)}` : `${monYear(f)}–${monYear(t)}`;
  if (from === to) return `${dayMon(f)} ${f.getFullYear()}`;
  return sameYear
    ? `${dayMon(f)} – ${dayMon(t)} ${t.getFullYear()}`
    : `${dayMon(f)} ${f.getFullYear()} – ${dayMon(t)} ${t.getFullYear()}`;
}

/** The ready-made periods, worked out from today each time the menu opens. */
function sections() {
  const n = new Date();
  const y = n.getFullYear();
  const m = n.getMonth();
  const span = (y1, m1, count) => ({
    from: ymd(new Date(y1, m1, 1)),
    to: ymd(new Date(y1, m1 + count, 0)),
  });
  const q = m - (m % 3);
  const fy = m >= 3 ? y : y - 1; // April to March
  return [
    { title: 'Recommended', items: [
      { label: 'This month', ...span(y, m, 1) },
      { label: 'Last month', ...span(y, m - 1, 1) },
    ] },
    { title: 'Relative dates', items: [
      { label: 'Today', from: today(), to: today() },
      { label: 'Last 7 days', from: daysAgo(6), to: today() },
      { label: 'Last 30 days', from: daysAgo(29), to: today() },
      { label: 'Last 90 days', from: daysAgo(89), to: today() },
    ] },
    { title: 'Calendar months', items: [
      { label: 'This month', ...span(y, m, 1) },
      { label: 'This quarter', ...span(y, q, 3) },
      { label: 'This year', ...span(y, 0, 12) },
      { label: 'Last month', ...span(y, m - 1, 1) },
      { label: 'Last quarter', ...span(y, q - 3, 3) },
      { label: 'Last 3 months', ...span(y, m - 3, 3) },
      { label: 'Last 6 months', ...span(y, m - 6, 6) },
      { label: 'Last 12 months', ...span(y, m - 12, 12) },
    ] },
    { title: 'Financial year', items: [
      { label: 'This financial year', ...span(fy, 3, 12) },
      { label: 'Last financial year', ...span(fy - 1, 3, 12) },
    ] },
  ];
}

const Calendar = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);
const Chevron = ({ dir = 'down' }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"
    style={{ transform: { down: 'none', left: 'rotate(90deg)', right: 'rotate(-90deg)' }[dir] }}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/**
 * The period button every report carries, and its menu: ready-made periods, previous / next
 * to step by the same length, and a custom range. `value` and `onChange` deal in { from, to }.
 * With `allowAll`, the menu also offers "All dates", which is a `value` of null.
 */
export default function PeriodPicker({ value, onChange, allowAll = false }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = () => {
    setDraft(value || thisMonth());
    setCustom(false);
    setError(null);
    setOpen((o) => !o);
  };
  const pick = (range) => { onChange(range && { from: range.from, to: range.to }); setOpen(false); };
  // "All dates" has no length to step by.
  const step = (dir) => value && onChange(shiftRange(value.from, value.to, dir));
  const apply = () => {
    if (!draft.from || !draft.to) return setError('Pick both dates.');
    if (draft.from > draft.to) return setError('The start date is after the end date.');
    return pick(draft);
  };
  const isOn = (r) => !!value && r.from === value.from && r.to === value.to;

  return (
    <div className="period" ref={ref}>
      <div className="period-bar">
        <button type="button" className="period-step" aria-label="Previous period" disabled={!value} onClick={() => step(-1)}>
          <Chevron dir="left" />
        </button>
        <button type="button" className="period-btn" aria-haspopup="dialog" aria-expanded={open} onClick={toggle}>
          <Calendar /><span>{value ? rangeLabel(value.from, value.to) : 'All dates'}</span><Chevron />
        </button>
        <button type="button" className="period-step" aria-label="Next period" disabled={!value} onClick={() => step(1)}>
          <Chevron dir="right" />
        </button>
      </div>

      {open && (
        <div className="period-menu" role="dialog" aria-label="Choose period">
          <div className="period-nav">
            <button type="button" disabled={!value} onClick={() => step(-1)}><Chevron dir="left" /> Previous</button>
            <button type="button" disabled={!value} onClick={() => step(1)}>Next <Chevron dir="right" /></button>
          </div>
          {allowAll && (
            <div className="period-group">
              <button type="button" className="period-opt" aria-pressed={!value} onClick={() => pick(null)}>
                <span>All dates</span><span className="period-range">no date filter</span>
              </button>
            </div>
          )}
          {sections().map((s) => (
            <div key={s.title} className="period-group">
              <div className="period-title">{s.title}</div>
              {s.items.map((r) => (
                <button key={r.label} type="button" className="period-opt" aria-pressed={isOn(r)} onClick={() => pick(r)}>
                  <span>{r.label}</span><span className="period-range">{rangeLabel(r.from, r.to)}</span>
                </button>
              ))}
            </div>
          ))}
          <div className="period-group">
            <button type="button" className="period-opt" aria-expanded={custom} onClick={() => setCustom((c) => !c)}>
              <span>Custom date range</span><Chevron dir={custom ? 'down' : 'right'} />
            </button>
            {custom && (
              <div className="period-custom">
                <label>From<input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
                <label>To<input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
                {error && <div className="period-error" role="alert">{error}</div>}
                <button type="button" className="btn small" onClick={apply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A list page's period, kept in the address so back, refresh and a shared link
 * all keep it: `from`/`to`, or `range=today|month|all` from the dashboard's links.
 * Without either it is this month, or all dates when `allByDefault` — for a view
 * of work still to do, where something waiting since last month must not vanish.
 * `query` is the from/to to hand the API ('' for all dates).
 */
export function useUrlPeriod({ allByDefault = false } = {}) {
  const [params, setParams] = useSearchParams();
  const from = params.get('from');
  const to = params.get('to');
  const range = params.get('range');
  let period;
  if (from && to) period = { from, to };
  else if (range === 'today') period = { from: today(), to: today() };
  else if (range === 'month') period = thisMonth();
  else if (range === 'all' || allByDefault) period = null;
  else period = thisMonth();

  const setPeriod = (p) => {
    const next = new URLSearchParams(params);
    ['from', 'to', 'range'].forEach((k) => next.delete(k));
    if (p) { next.set('from', p.from); next.set('to', p.to); } else next.set('range', 'all');
    setParams(next);
  };
  const query = period ? `from=${period.from}&to=${period.to}` : '';
  return { period, setPeriod, query };
}
